import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEO/SEOHead';
import { BlogCategory, BlogPost } from '../types/Blog';
import { Gallery } from '../types/Gallery';
import { loadLiveBlogData } from '../utils/BlogAPI';
import { formatStoryDate, postCategorySlugs, publishedPosts } from '../utils/blogPresentation';
import { loadLiveGalleries } from '../utils/GalleryAPI';
import { recordEvent } from '../utils/analytics';
import { articleHeadings, galleryCardDelivery, sanitizeBlogHtml, selectPostGalleries, postGalleryFilter } from '../utils/blogHtml';
import { cloudinaryDeliveryUrl, cloudinarySrcSet } from '../utils/imageSeo';
import { bindPhotoMasonry } from '../utils/photoMasonry';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import { API_BASE_URL } from '../utils/apiBase';
import sitePaths from '../utils/sitePaths';
import BlogMetaShare from '../components/Sharing/BlogMetaShare';
import BlogShareRow from '../components/Sharing/BlogShareRow';
import { openWhatsAppChat } from '../utils/whatsappContact';
import { whatsappDigits, whatsappHref } from '../utils/localBusiness';
import './BlogJournal.css';

interface BlogPostPageProps {
  preview?: boolean;
}

function BlogArticleBody({ html }: { html: string }) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!node) return undefined;
    return bindPhotoMasonry(node);
  }, [node, html]);
  return <div className="blog-body" ref={setNode} dangerouslySetInnerHTML={{ __html: html }} />;
}

function readBootstrap(slug?: string) {
  if (typeof document === 'undefined') return null;
  const node = document.getElementById('page-bootstrap');
  if (!node?.textContent) return null;
  try {
    const data = JSON.parse(node.textContent);
    if (data?.kind === 'post' && data.post?.slug === slug) return data;
  } catch (error) {
    return null;
  }
  return null;
}

function readingMinutes(post: BlogPost): number {
  const words = String(post.content || '').replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

function sameDay(left?: string, right?: string): boolean {
  if (!left || !right) return false;
  const a = new Date(left);
  const b = new Date(right);
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
}

const BlogPostPage: React.FC<BlogPostPageProps> = ({ preview = false }) => {
  const { slug = '' } = useParams();
  const websiteContent = useWebsiteContent();
  const seeded = readBootstrap(slug);
  const [post, setPost] = useState<BlogPost | null>(seeded?.post || null);
  const [categories, setCategories] = useState<BlogCategory[]>(
    Array.isArray(seeded?.categories) && seeded.categories.length
      ? seeded.categories
      : (seeded?.category ? [seeded.category] : [])
  );
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [missing, setMissing] = useState(false);
  const [previewAllowed, setPreviewAllowed] = useState(!preview || Boolean(seeded?.post));

  useEffect(() => {
    if (!preview || seeded?.post) return undefined;
    let active = true;
    fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
      .then((response) => {
        if (!active) return;
        setPreviewAllowed(response.ok);
      })
      .catch(() => {
        if (!active) return;
        setPreviewAllowed(false);
      });
    return () => {
      active = false;
    };
  }, [preview, seeded]);

  useEffect(() => {
    if (preview && !previewAllowed) return undefined;
    let active = true;
    Promise.all([loadLiveBlogData(), loadLiveGalleries('cards').catch(() => [])])
      .then(([{ posts: loadedPosts, categories: loadedCategories }, loadedGalleries]) => {
        if (!active) return;
        const loaded = Array.isArray(loadedPosts) ? loadedPosts : [];
        const published = publishedPosts(loaded);
        if (published.length) setPosts(published);
        if (Array.isArray(loadedCategories) && loadedCategories.length) setCategories(loadedCategories);
        if (Array.isArray(loadedGalleries) && loadedGalleries.length) setGalleries(loadedGalleries);
        const found = loaded.find((item) => item.slug === slug) || null;
        if (preview) {
          const match = found || seeded?.post || null;
          if (match) {
            setPost(match);
            setMissing(false);
          } else {
            setPost(null);
            setMissing(true);
          }
          return;
        }
        const publishedMatch = Boolean(
          found && (published.some((item) => item.slug === slug) || found.status === 'published')
        );
        if (publishedMatch && found) {
          setPost(found);
          setMissing(false);
        } else if (seeded?.post) {
          setMissing(false);
        } else {
          setPost(null);
          setMissing(true);
        }
      })
      .catch(() => {
        if (!active) return;
        if (preview && !seeded?.post) setMissing(true);
      });
    return () => {
      active = false;
    };
  }, [preview, previewAllowed, slug]);

  useEffect(() => {
    if (!post || preview || post.noindex) return;
    recordEvent('view_blog_post', { slug: post.slug, category: post.category || '' });
  }, [post, preview]);

  const assigned = post
    ? postCategorySlugs(post)
      .map((slug) => categories.find((item) => item.slug === slug) || (seeded?.category?.slug === slug ? seeded.category : null))
      .filter((item): item is BlogCategory => Boolean(item))
    : [];
  const related = useMemo(() => {
    if (!post) return [];
    const mine = new Set(postCategorySlugs(post));
    return posts.filter((item) => item.slug !== post.slug && postCategorySlugs(item).some((slug) => mine.has(slug))).slice(0, 3);
  }, [post, posts]);
  const index = posts.findIndex((item) => item.slug === post?.slug);
  const previous = index >= 0 ? posts[index + 1] : null;
  const next = index > 0 ? posts[index - 1] : null;

  if (preview && !previewAllowed) {
    return (
      <main className="blog-page page-content journal-index">
        <SEOHead title="Preview" noIndex exactTitle />
        <h1>Sign in to preview</h1>
        <p className="journal-standfirst">This story preview is only available to the studio.</p>
        <p><Link to="/admin">Studio sign in</Link></p>
      </main>
    );
  }

  if (!post) {
    if (missing) {
      return (
        <main className="blog-page page-content journal-index">
          <h1>Page not found</h1>
          <p className="journal-standfirst">This story is not available.</p>
          <p className="journal-empty"><Link to="/blog">Back to the blog</Link></p>
        </main>
      );
    }
    return <main className="blog-page page-content journal-post"><p className="journal-intro">Loading story…</p></main>;
  }

  const canonical = `https://carmenlorraine.com/blog/${post.slug}`;
  const title = post.seoTitle || `${post.title} | Cape Town Photographer`;
  const description = post.metaDescription || String(post.excerpt || '').slice(0, 155);
  const image = post.heroImage?.secure_url || post.heroImage?.url || '';
  const updated = post.updatedAt && !sameDay(post.updatedAt, post.publishedAt);
  const filter = postGalleryFilter(post);
  const chosen = selectPostGalleries(post, galleries) as Gallery[];
  const bodyHtml = sanitizeBlogHtml(post.content || '');
  const headings = articleHeadings(bodyHtml);
  const shareTitle = post.h1 || post.title;

  return (
    <div className="blog-page">
      <SEOHead
        exactTitle
        title={title}
        description={description}
        image={image || undefined}
        canonicalUrl={preview ? undefined : canonical}
        url={canonical}
        noIndex={preview || Boolean(post.noindex)}
        type="article"
        author={post.author || 'Carmen Lorraine'}
        publishedTime={post.publishedAt}
        modifiedTime={post.updatedAt || post.publishedAt}
      />
      <article className="journal-post">
        <header className="journal-post-hero">
          <div className="journal-post-heading">
            {assigned.length ? (
              <p className="journal-kicker">
                {assigned.map((item, index) => (
                  <span key={item.slug}>
                    {index > 0 ? <span aria-hidden="true"> · </span> : null}
                    <Link to={`/blog/category/${item.slug}`}>{item.name}</Link>
                  </span>
                ))}
              </p>
            ) : null}
            <h1>{post.h1 || post.title}</h1>
            {post.excerpt ? <p className="journal-intro">{post.excerpt}</p> : null}
            <p className="blog-meta">
              <time dateTime={post.publishedAt}>{formatStoryDate(post.publishedAt)}</time>
              {updated ? <span>Updated <time dateTime={post.updatedAt}>{formatStoryDate(post.updatedAt)}</time></span> : null}
              <span>{readingMinutes(post)} min read</span>
              <span className="blog-meta-tail">
                <span>{post.author || 'Carmen Lorraine'}</span>
                <BlogMetaShare slug={post.slug} title={shareTitle} />
              </span>
            </p>
          </div>
          {image ? (
            <figure className="blog-hero">
              <img
                src={cloudinaryDeliveryUrl(image, 1600) || image}
                srcSet={cloudinarySrcSet(image, [640, 960, 1200, 1600, 2400])}
                sizes="(max-width: 720px) calc(100vw - 1.5rem), min(72rem, calc(100vw - 2.5rem))"
                alt={post.heroImage?.alt || post.featuredImageAlt || post.title}
                width={post.heroImage?.width || 1600}
                height={post.heroImage?.height || 1067}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              {post.heroImage?.caption ? <figcaption>{post.heroImage.caption}</figcaption> : null}
            </figure>
          ) : null}
        </header>
        <div className="journal-column">
          {post.showToc !== false && headings.length > 1 ? (
            <nav className="blog-toc" aria-label="On this page">
              <p>On this page</p>
              <ol>
                {headings.map((heading) => (
                  <li key={heading.id}><a href={`#${heading.id}`}>{heading.text}</a></li>
                ))}
              </ol>
            </nav>
          ) : null}
          <BlogArticleBody html={bodyHtml} />
          {(post.faq || []).some((item) => item.question && item.answer) ? (
            <section className="blog-faq">
              <h2>Questions</h2>
              {(post.faq || []).filter((item) => item.question && item.answer).map((item) => (
                <div key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          ) : null}
          <BlogShareRow position="bottom" slug={post.slug} title={shareTitle} />
          <aside className="blog-cta">
            <p className="blog-cta-invite">If you would like to talk about a session, send a note.</p>
            <p>
              <a
                className="blog-cta-button blog-cta-whatsapp"
                href={whatsappHref(websiteContent.contactPhone, `Hi Carmen, I was reading “${post.h1 || post.title}” and I’d like to get in touch.`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.preventDefault();
                  recordEvent('generate_lead', { method: 'whatsapp', source: 'blog_post', slug: post.slug });
                  openWhatsAppChat(
                    whatsappDigits(websiteContent.contactPhone),
                    `Hi Carmen, I was reading “${post.h1 || post.title}” and I’d like to get in touch.`
                  );
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path fill="currentColor" d="M20.5 3.5A11 11 0 0 0 2.1 17.2L1 23l6-1.6A11 11 0 0 0 12 23a11 11 0 0 0 8.5-19.5zM12 21a9 9 0 0 1-4.6-1.3l-.3-.2-3.5.9.9-3.4-.2-.3A9 9 0 1 1 12 21zm5-6.7c-.3-.1-1.6-.8-1.8-.9s-.4-.1-.6.1-.7.9-.8 1-.3.2-.6.1a7.4 7.4 0 0 1-2.2-1.4 8.2 8.2 0 0 1-1.5-1.9c-.2-.3 0-.4.1-.6l.4-.5.2-.3a.5.5 0 0 0 0-.5c0-.1-.6-1.4-.8-1.9s-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 15 15 0 0 0 1.5.6 3.6 3.6 0 0 0 1.7.1 2.7 2.7 0 0 0 1.8-1.3 2.2 2.2 0 0 0 .2-1.3c-.1-.1-.3-.2-.6-.3z" />
                </svg>
                Contact me
              </a>
            </p>
          </aside>
          {chosen.length ? (
            <aside className="blog-gallery-more">
              <h2>{filter ? `See more ${filter.name} photos` : 'See more photographs'}</h2>
              <ul>
                {chosen.map((gallery: Gallery, index) => {
                  const photo = gallery.images?.[0];
                  const src = photo?.secure_url || photo?.url || '';
                  const delivery = src ? galleryCardDelivery(src, index === 0) : null;
                  return (
                    <li key={gallery.id}>
                      <Link to={sitePaths.galleryPath(gallery)}>
                        {delivery ? (
                          <img
                            src={delivery.src}
                            srcSet={delivery.srcSet || undefined}
                            sizes={delivery.srcSet ? delivery.sizes : undefined}
                            alt={photo?.alt || photo?.context?.caption || gallery.name}
                            width={photo?.width || undefined}
                            height={photo?.height || undefined}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : null}
                        <span>{gallery.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <p><Link to={filter ? `/galleries/${filter.slug}` : '/galleries'}>View the {filter ? filter.name : 'galleries'}</Link></p>
            </aside>
          ) : null}
          {related.length ? (
            <aside className="blog-related">
              <h2>Related posts</h2>
              <ul>
                {related.map((item) => <li key={item.id}><Link to={`/blog/${item.slug}`}>{item.title}</Link></li>)}
              </ul>
            </aside>
          ) : null}
          {previous || next ? (
            <nav className="blog-nearby" aria-label="More stories">
              {previous ? <Link className="is-prev" to={`/blog/${previous.slug}`}><span>Previous</span>{previous.title}</Link> : <span />}
              {next ? <Link className="is-next" to={`/blog/${next.slug}`}><span>Next</span>{next.title}</Link> : null}
            </nav>
          ) : null}
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
